/**
 * Génération du récapitulatif PDF d'un virement.
 *
 * Aucune dépendance externe : un PDF 1.4 minimal est écrit directement, ce qui
 * garantit un document lisible dans tous les lecteurs, hors ligne, sans service
 * tiers et sans exposer de données à un générateur distant.
 */

export interface TransferPdfData {
  reference: string;
  beneficiaryName: string;
  email: string;
  bankName: string;
  ibanMasked: string;
  amount: number;
  currency: string;
  statusLabel: string;
  createdAt: string;
  verifiedAt: string | null;
  link: string;
  linkActive: boolean;
  note: string | null;
  clientName: string | null;
  clientLogin: string | null;
  language: string;
}

const PAGE = { width: 595.28, height: 841.89, margin: 56 };
const RIGHT = PAGE.width - PAGE.margin;

const DARK = '0.07 0.07 0.07 rg';
const GREY = '0.44 0.44 0.44 rg';
const LIGHT = '0.84 0.84 0.84 RG';
const WHITE = '1 1 1 rg';

const SPECIALS: Record<string, number> = {
  '€': 0x80,
  '‚': 0x82,
  'ƒ': 0x83,
  '„': 0x84,
  '…': 0x85,
  '†': 0x86,
  '‡': 0x87,
  'ˆ': 0x88,
  '‰': 0x89,
  '‹': 0x8b,
  '‘': 0x91,
  '’': 0x92,
  '“': 0x93,
  '”': 0x94,
  '•': 0x95,
  '–': 0x96,
  '—': 0x97,
  '™': 0x99,
  '›': 0x9b,
};

function toWinAnsi(value: string): string {
  let out = '';
  for (const char of value) {
    const code = char.codePointAt(0) ?? 63;
    if (code === 10) {
      out += String.fromCharCode(10);
      continue;
    }
    if (code >= 32 && code <= 255) {
      out += String.fromCharCode(code);
      continue;
    }
    const mapped = SPECIALS[char];
    out += String.fromCharCode(mapped ?? 63);
  }
  return out;
}

function escapeText(value: string): string {
  return toWinAnsi(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function textOp(x: number, y: number, size: number, bold: boolean, color: string, value: string): string {
  const font = bold ? 'F2' : 'F1';
  return `${color}\nBT /${font} ${size} Tf 1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm (${escapeText(value)}) Tj ET\n`;
}

function ruleOp(y: number): string {
  return `${LIGHT}\n0.7 w ${PAGE.margin} ${y.toFixed(2)} m ${RIGHT.toFixed(2)} ${y.toFixed(2)} l S\n`;
}

function wrap(value: string, maxChars: number): string[] {
  const words = String(value ?? '').split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

function formatDate(value: string | null, language: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 16).replace('T', ' ');
  }
}

function formatAmount(amount: number, currency: string, language: string): string {
  try {
    return new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'fr-FR', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function assemble(content: string): Uint8Array {
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE.width} ${PAGE.height}] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> >> /Contents 6 0 R >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  ];

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  const bytes = new Uint8Array(pdf.length);
  for (let index = 0; index < pdf.length; index += 1) {
    bytes[index] = pdf.charCodeAt(index) & 0xff;
  }
  return bytes;
}

export function buildTransferPdf(data: TransferPdfData): Blob {
  const isEnglish = data.language === 'en';
  const label = (fr: string, en: string) => (isEnglish ? en : fr);

  const ops: string[] = [];

  // En-tête sombre
  ops.push('0.05 0.09 0.13 rg');
  ops.push(`0 ${(PAGE.height - 64).toFixed(2)} ${PAGE.width} 64 re f\n`);
  ops.push(textOp(PAGE.margin, PAGE.height - 40, 17, true, WHITE, 'Monexa'));
  ops.push(
    textOp(PAGE.margin, PAGE.height - 56, 8.5, false, '0.72 0.76 0.8 rg', label('Documents bancaires', 'Banking documents')),
  );

  let y = PAGE.height - 110;

  ops.push(textOp(PAGE.margin, y, 20, true, DARK, label('Récapitulatif de virement', 'Transfer summary')));
  y -= 20;
  ops.push(
    textOp(
      PAGE.margin,
      y,
      9.5,
      false,
      GREY,
      `${label('Référence', 'Reference')} ${data.reference}`,
    ),
  );

  y -= 34;
  ops.push(textOp(PAGE.margin, y, 8.5, false, GREY, label('MONTANT DU VIREMENT', 'TRANSFER AMOUNT')));
  y -= 30;
  ops.push(textOp(PAGE.margin, y, 26, true, DARK, formatAmount(data.amount, data.currency, data.language)));
  y -= 26;
  ops.push(ruleOp(y));

  const rows: { label: string; value: string }[] = [
    { label: label('Bénéficiaire', 'Beneficiary'), value: data.beneficiaryName || '—' },
    { label: label('E-mail du bénéficiaire', 'Beneficiary e-mail'), value: data.email || '—' },
    { label: label('Banque bénéficiaire', 'Beneficiary bank'), value: data.bankName || '—' },
    { label: label('Compte destinataire', 'Destination account'), value: data.ibanMasked || '—' },
    { label: label('Statut de l’opération', 'Operation status'), value: data.statusLabel },
    { label: label('Créé le', 'Created on'), value: formatDate(data.createdAt, data.language) },
    {
      label: label('Authentifié le', 'Authenticated on'),
      value: data.verifiedAt ? formatDate(data.verifiedAt, data.language) : label('En attente', 'Pending'),
    },
    { label: label('Fiche client associée', 'Linked client record'), value: data.clientName || '—' },
    { label: label('Identifiant bancaire du client', 'Client bank login'), value: data.clientLogin || '—' },
    { label: label('Note interne', 'Internal note'), value: data.note || '—' },
  ];

  for (const row of rows) {
    y -= 30;
    ops.push(textOp(PAGE.margin, y, 8, false, GREY, row.label.toUpperCase()));
    y -= 14;
    const valueLines = wrap(row.value, 78);
    for (const valueLine of valueLines.slice(0, 2)) {
      ops.push(textOp(PAGE.margin, y, 11, true, DARK, valueLine));
      y -= 13;
    }
    y += 13;
    y -= 4;
    ops.push(ruleOp(y));
  }

  y -= 30;
  ops.push(textOp(PAGE.margin, y, 8, false, GREY, label('LIEN UNIQUE DU VIREMENT', 'UNIQUE TRANSFER LINK')));
  y -= 14;
  ops.push(
    textOp(
      PAGE.margin,
      y,
      10,
      true,
      DARK,
      `${label('Statut du lien :', 'Link status:')} ${data.linkActive ? label('Actif', 'Active') : label('Désactivé', 'Disabled')}`,
    ),
  );
  y -= 14;
  for (const linkLine of wrap(data.link, 82).slice(0, 3)) {
    ops.push(textOp(PAGE.margin, y, 8.5, false, GREY, linkLine));
    y -= 11;
  }

  ops.push(ruleOp(PAGE.margin + 4));
  ops.push(
    textOp(
      PAGE.margin,
      56,
      8,
      false,
      GREY,
      label(
        'Document généré automatiquement par la plateforme Monexa. Ne pas diffuser.',
        'Automatically generated by the Monexa platform. Do not distribute.',
      ),
    ),
  );

  return new Blob([assemble(ops.join(''))], { type: 'application/pdf' });
}

function safeFileName(reference: string): string {
  const cleaned = String(reference ?? 'virement').replace(/[^A-Za-z0-9_-]+/g, '-');
  return `recapitulatif-virement-${cleaned}.pdf`;
}

export function downloadTransferPdf(data: TransferPdfData): void {
  const blob = buildTransferPdf(data);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = safeFileName(data.reference);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function openTransferPdf(data: TransferPdfData): void {
  const blob = buildTransferPdf(data);
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    downloadTransferPdf(data);
    URL.revokeObjectURL(url);
    return;
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}